import React, { Dispatch, SetStateAction } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { SkeletonItem } from '@atlaskit/menu';
import Checkbox from '@atlaskit/checkbox';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import OfficeBuildingFilledIcon from '@atlaskit/icon/glyph/office-building-filled';
import { CustomPanel } from '@css-xp/ppc-purchase-experience';
import { CloudSiteItemV2 } from './cloud-site-item-v2';
import { EntitlementContainer } from './entitlement-container';
import {
  SelectType,
  ExternalProductsList,
  CloudSiteOrOrgNameMapGroupedByBillingProfile,
  CloudSiteOrOrgNameMap,
} from '../types';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { N300 } from '@atlaskit/theme/colors';
import { NoEntitlement } from './no-entitlement';
import { State } from '../../../../store/types';
import './cloud-site-container.scss';
import { ExternalProductsIndicatorContainer } from './external-products-indicator/external-products-indicator-container';
import { ErrorEntitlement } from './error-entitlement';
import { ROVO_PRODUCT_ID } from '@css-xp/ppc-constants';
import isEmpty from 'lodash/isEmpty';
import { useOrgProductInstancesMap } from '../../../../store';
import { CartProductSchema } from '../../../../types';

const CloudSiteStyles = styled.div({
  height: '400px',
  overflowY: 'auto',
  marginRight: '-15px',
  paddingRight: '15px',
  cursor: 'pointer',
});
interface Props {
  cloudSiteOrOrgNameMap?: CloudSiteOrOrgNameMap | null;
  externalProductsList?: ExternalProductsList;
  productsGroupedByBillingProfileAndCloudSite?: CloudSiteOrOrgNameMapGroupedByBillingProfile | null;
  onChange: (type: SelectType, identifier: string, status: boolean) => void;
  isLoading?: boolean;
  isSelectionDisabled?: boolean;
  showNoEntitlement?: boolean;
  billingProfiles?: State['billingProfiles'];
  isBillingProfileLoading?: boolean;
  isFetchEntitlementsLoading?: boolean;
  isExternalProductListLoading?: boolean;
  isEntitlementListWithEmailLoading?: boolean;
  showTransferForm?: boolean;
  isRenderingTransferFlowEntitlements?: boolean;
  isRenderingExternalCloudSiteEntitlements?: boolean;
  onTransferSearchFormSubmit?: (data: any, currentCloudSiteHostname?: string) => Promise<void>;
  showErrorState?: boolean;
  handleRefetchEntitlements?: () => void;
  showInactiveProducts?: boolean;
  shouldShowAddons?: boolean;
  shouldShowSandboxStatus?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isCheckboxSelectionEnabled?: boolean;
  checkedItemMap?: Record<string, SelectType>;
  setCheckedItemMap?: Dispatch<SetStateAction<Record<string, SelectType>>>;
  hideFoundLabel?: boolean;
  hideExternalListButton?: boolean;
  alreadyCheckedItemMap?: Record<string, SelectType>;
  setAlreadyCheckedItemMap?: Dispatch<SetStateAction<Record<string, SelectType>>>;
  isDefaultExpanded?: boolean;
  isSingleProductSelection?: boolean;
  isTWCPhase2Enabled?: boolean;
}

const renderLoading = (
  <div className="cloud-site-container-loader">
    <LoadingIndicator size="medium" />
  </div>
);

const getCheckBoxPrefix = ({
  isChecked,
  onChange,
  name,
  isDisabled,
  cloudSiteOrOrgNameMap,
  checkedItemMap,
  setCheckedItemMap,
  isTWCPhase2Enabled,
  orgProductInstancesMap = {},
}: {
  isChecked: boolean;
  onChange: (name: string, status: boolean) => void;
  name: string;
  isDisabled: boolean;
  cloudSiteOrOrgNameMap?: CloudSiteOrOrgNameMap;
  checkedItemMap: Record<string, SelectType>;
  setCheckedItemMap: Dispatch<SetStateAction<Record<string, SelectType>>>;
  isTWCPhase2Enabled?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
}) => {
  const _onChange = (isSelected: boolean) => {
    onChange(name, isSelected);
    if (checkedItemMap[name]) {
      //remove all the related site products
      setCheckedItemMap((prevCheckedItemMap: Record<string, SelectType>) => {
        const newCheckedItemMap = { ...prevCheckedItemMap };
        delete newCheckedItemMap[name];
        (cloudSiteOrOrgNameMap?.[name]?.products as { id: string }[])?.forEach((product: { id: string }) => {
          delete newCheckedItemMap[product.id];
        });
        return newCheckedItemMap;
      });
    } else {
      // add all the related site products
      setCheckedItemMap((prevCheckedItemMap: Record<string, SelectType>) => {
        const newCheckedItemMap = { ...prevCheckedItemMap };
        newCheckedItemMap[name] = SelectType.SITE;
        (cloudSiteOrOrgNameMap?.[name]?.products as { id: string; parentEntitlementId: string }[])?.forEach(
          (product: { id: string; parentEntitlementId: string }) => {
            if (isTWCPhase2Enabled) {
              if (
                !(
                  product.parentEntitlementId &&
                  Object.keys(orgProductInstancesMap).includes(product.parentEntitlementId)
                )
              ) {
                newCheckedItemMap[product.id] = SelectType.ENTITLEMENT;
              }
            } else {
              newCheckedItemMap[product.id] = SelectType.ENTITLEMENT;
            }
          }
        );
        return newCheckedItemMap;
      });
    }
  };

  return (
    <Checkbox
      isChecked={isChecked}
      onChange={() => _onChange(!isChecked)}
      label={''}
      value="Controlled Checkbox"
      name="controlled-checkbox"
      isDisabled={isDisabled}
    />
  );
};

const getCheckedSiteStatus = ({
  checkedItemMap,
  cloudSiteOrOrgName,
  cloudSiteOrOrgNameMap,
  alreadyCheckedItemMap,
  isTWCPhase2Enabled,
  orgProductInstancesMap = {},
}: {
  checkedItemMap: Record<string, SelectType>;
  cloudSiteOrOrgName: string;
  cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap;
  alreadyCheckedItemMap?: Record<string, SelectType>;
  isTWCPhase2Enabled?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
}) => {
  const productsGroupedByCloudSiteOrOrgName = cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products;
  const productsGroupedByCloudSiteOrOrgNameWithoutOrgInstances = productsGroupedByCloudSiteOrOrgName?.filter(
    (product) =>
      !(product.parentEntitlementId && Object.keys(orgProductInstancesMap).includes(product.parentEntitlementId))
  );
  const productsInCloudSiteOrOrg = isTWCPhase2Enabled
    ? productsGroupedByCloudSiteOrOrgNameWithoutOrgInstances
    : productsGroupedByCloudSiteOrOrgName;

  //checking for both temporary selected items (checkedItemMap) and already selected items on cart (alreadyCheckedItemMap)
  const productIds = productsInCloudSiteOrOrg?.map((product) => product.id);
  if (productIds?.length) {
    return productIds.every((productId) => checkedItemMap?.[productId] ?? alreadyCheckedItemMap?.[productId]);
  }
  return (
    !!(checkedItemMap && checkedItemMap[cloudSiteOrOrgName]) ||
    !!(alreadyCheckedItemMap && alreadyCheckedItemMap[cloudSiteOrOrgName])
  );
};

//This function is used to disable the selection of product which is already added in the cart.
const getCheckedAndDisabledState = (
  alreadyCheckedItemMap: Record<string, SelectType> | undefined,
  cloudSiteOrOrgName: string,
  cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap
) => {
  const productIds = cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products?.map((product) => product.id);
  if (productIds && productIds.length) {
    return productIds.every((productId) => !!(alreadyCheckedItemMap && alreadyCheckedItemMap[productId]));
  }

  return !!(alreadyCheckedItemMap && alreadyCheckedItemMap[cloudSiteOrOrgName]);
};

const ProductsGroupedByCloudSite = ({
  cloudSiteOrOrgNameMap,
  externalProductsList,
  onChange,
  isSelectionDisabled,
  onTransferSearchFormSubmit,
  isRenderingTransferFlowEntitlements = false,
  showInactiveProducts = false,
  shouldShowAddons = false,
  shouldShowSandboxStatus = false,
  isJPDAsJiraFamilyEnabled = false,
  isCheckboxSelectionEnabled,
  alreadyCheckedItemMap,
  checkedItemMap,
  setCheckedItemMap,
  hideExternalListButton = false,
  isDefaultExpanded,
  isSingleProductSelection,
  isTWCPhase2Enabled,
  orgProductInstancesMap,
}: {
  cloudSiteOrOrgNameMap?:
    | CloudSiteOrOrgNameMap
    | CloudSiteOrOrgNameMapGroupedByBillingProfile[number]['cloudSiteOrOrgNameMap']
    | null;
  externalProductsList?: ExternalProductsList;
  onChange: Props['onChange'];
  isSelectionDisabled: boolean;
  isRenderingTransferFlowEntitlements?: boolean;
  onTransferSearchFormSubmit?: Props['onTransferSearchFormSubmit'];
  showInactiveProducts?: boolean;
  shouldShowAddons?: boolean;
  shouldShowSandboxStatus?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isCheckboxSelectionEnabled?: boolean;
  alreadyCheckedItemMap?: Record<string, SelectType>;
  checkedItemMap?: Record<string, SelectType>;
  setCheckedItemMap?: Dispatch<SetStateAction<Record<string, SelectType>>>;
  hideExternalListButton?: boolean;
  isDefaultExpanded?: boolean;
  isSingleProductSelection?: boolean;
  isTWCPhase2Enabled?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
}) => {
  if (!cloudSiteOrOrgNameMap) return null;
  const cloudSiteOrOrgNames = (cloudSiteOrOrgNameMap && Object.keys(cloudSiteOrOrgNameMap)) || [];
  const sortedCloudSitOrOrgNames = cloudSiteOrOrgNames
    ?.filter((name) =>
      showInactiveProducts
        ? cloudSiteOrOrgNameMap[name].products.length + cloudSiteOrOrgNameMap[name].inactiveProducts.length
        : cloudSiteOrOrgNameMap[name].products.length
    )
    .sort((a, b) => (a < b ? -1 : 1));

  const orgNames = Object.values(cloudSiteOrOrgNameMap)
    .flatMap((cloudSiteOrOrgName) => [...cloudSiteOrOrgName.products, ...cloudSiteOrOrgName.inactiveProducts])
    .filter((product) => product?.organisationName)
    ?.map((product) => product.organisationName);

  const productsGroupedByCloudSite = sortedCloudSitOrOrgNames.map((cloudSiteOrOrgName, index) => {
    return (
      <CustomPanel
        key={cloudSiteOrOrgName}
        toggleButtonOnLeft={true}
        isSelected={cloudSiteOrOrgNameMap[cloudSiteOrOrgName].isChecked === true}
        isCustomPanelV2Enabled={true}
        {...(!isSingleProductSelection &&
          isCheckboxSelectionEnabled &&
          checkedItemMap &&
          setCheckedItemMap && {
            checkboxPrefix: getCheckBoxPrefix({
              isChecked: getCheckedSiteStatus({
                checkedItemMap,
                cloudSiteOrOrgName,
                cloudSiteOrOrgNameMap,
                alreadyCheckedItemMap,
                isTWCPhase2Enabled,
                orgProductInstancesMap,
              }),
              onChange: onChange.bind(null, SelectType.SITE),
              name: cloudSiteOrOrgName,
              cloudSiteOrOrgNameMap: cloudSiteOrOrgNameMap,
              checkedItemMap: checkedItemMap,
              setCheckedItemMap: setCheckedItemMap,
              isTWCPhase2Enabled,
              orgProductInstancesMap,
              isDisabled:
                isSelectionDisabled ||
                (cloudSiteOrOrgNameMap &&
                  cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products?.some(
                    (product) => product.productId === ROVO_PRODUCT_ID && !product.isOrgProduct
                  )) ||
                (showInactiveProducts &&
                  cloudSiteOrOrgNameMap &&
                  !cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products?.length) ||
                getCheckedAndDisabledState(alreadyCheckedItemMap, cloudSiteOrOrgName, cloudSiteOrOrgNameMap),
            }),
          })}
        heading={
          <div className="pv-20 cloud-site-container-heading">
            <CloudSiteItemV2
              name={cloudSiteOrOrgName}
              isChecked={cloudSiteOrOrgNameMap ? cloudSiteOrOrgNameMap[cloudSiteOrOrgName].isChecked : false}
              onChange={onChange.bind(null, SelectType.SITE)}
              isDisabled={
                isSelectionDisabled ||
                (cloudSiteOrOrgNameMap &&
                  cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products?.some(
                    (product) => product.productId === ROVO_PRODUCT_ID && !product.isOrgProduct
                  )) ||
                (showInactiveProducts &&
                  cloudSiteOrOrgNameMap &&
                  !cloudSiteOrOrgNameMap[cloudSiteOrOrgName]?.products?.length)
              }
              isSelectionFromButtonDisabled={isCheckboxSelectionEnabled}
            />
          </div>
        }
        toggleButtonSuffix={
          externalProductsList && !hideExternalListButton ? (
            <ExternalProductsIndicatorContainer
              externalProductsList={externalProductsList}
              currentCloudSiteHostname={cloudSiteOrOrgName}
              isTransfer={isRenderingTransferFlowEntitlements}
              availableEntitlementIds={cloudSiteOrOrgNameMap[cloudSiteOrOrgName].products?.map((p) => p.id)}
              onTransferSearchFormSubmit={onTransferSearchFormSubmit!}
            />
          ) : null
        }
        isDefaultExpanded={index === 0}
        testId={`cloud-site-container-heading-${index}`}
        iconBefore={orgNames.includes(cloudSiteOrOrgName) && <OfficeBuildingFilledIcon label="" />}
      >
        <div className={classNames('ml-24 cloud-site-entitlement cloud-site-entitlement-container')}>
          <EntitlementContainer
            isSelectionDisabled={isSelectionDisabled}
            list={cloudSiteOrOrgNameMap[cloudSiteOrOrgName].products}
            inactiveList={cloudSiteOrOrgNameMap[cloudSiteOrOrgName].inactiveProducts}
            onChange={onChange.bind(null, SelectType.ENTITLEMENT)}
            cloudSiteHost={cloudSiteOrOrgName}
            isAddNewProductBtnEnabled={!orgNames.includes(cloudSiteOrOrgName) && !isCheckboxSelectionEnabled}
            shouldShowAppsCount={true}
            shouldShowTrialStatus={true}
            showInactiveProducts={showInactiveProducts}
            shouldShowAddons={shouldShowAddons}
            shouldShowSandboxStatus={shouldShowSandboxStatus}
            isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
            checkedItemMap={checkedItemMap}
            alreadyCheckedItemMap={alreadyCheckedItemMap}
            version={isCheckboxSelectionEnabled ? 2 : 1}
            showExternalListMessage={hideExternalListButton}
            externalProductsList={externalProductsList}
            isDefaultExpanded={isDefaultExpanded}
            isSingleProductSelection={isSingleProductSelection}
            isTWCPhase2Enabled={isTWCPhase2Enabled}
          />
        </div>
      </CustomPanel>
    );
  });

  return <>{productsGroupedByCloudSite}</>;
};

const BillingProfileNameWrapper = styled.div({
  color: N300,
  fontWeight: 600,
  lineHeight: '16px',
  textTransform: 'uppercase',
  fontSize: '12px',
});

/**
 * @param checkedItemMap - A map of checked items on the update existing entitlement modal.
 * @param alreadyCheckedItemMap - A map of already checked items, used to check already selected items in the cart on the update existing entitlement modal.
 */
export const CloudSiteContainer = ({
  cloudSiteOrOrgNameMap,
  externalProductsList,
  isExternalProductListLoading,
  isEntitlementListWithEmailLoading,
  onChange,
  isLoading = false,
  isSelectionDisabled = false,
  // show NoEntitlementComponent only when its required
  showNoEntitlement = false,
  showTransferForm = false,
  isRenderingTransferFlowEntitlements = false,
  billingProfiles,
  productsGroupedByBillingProfileAndCloudSite,
  onTransferSearchFormSubmit,
  isBillingProfileLoading = false,
  isFetchEntitlementsLoading = false,
  isRenderingExternalCloudSiteEntitlements = false,
  showErrorState = false,
  handleRefetchEntitlements,
  showInactiveProducts = false,
  shouldShowAddons = false,
  shouldShowSandboxStatus = false,
  isJPDAsJiraFamilyEnabled = false,
  isCheckboxSelectionEnabled = false,
  checkedItemMap,
  setCheckedItemMap,
  hideFoundLabel = false,
  hideExternalListButton = false,
  alreadyCheckedItemMap,
  isDefaultExpanded = false,
  isSingleProductSelection,
  isTWCPhase2Enabled,
}: Props) => {
  const orgProductInstancesMap = useOrgProductInstancesMap();
  const content = (() => {
    if (!isRenderingTransferFlowEntitlements && !isRenderingExternalCloudSiteEntitlements) {
      if (showErrorState) {
        return <ErrorEntitlement handleRefetchEntitlements={handleRefetchEntitlements} />;
      }

      // billing profiles have been fetched
      // now we will loading entitlements and then fetch the list of external products
      // we will show billing profiles with a loader while the network waterfall completes
      if (!isCheckboxSelectionEnabled) {
        if (isBillingProfileLoading) {
          return <>{renderLoading}</>;
        }
        if (isFetchEntitlementsLoading || isExternalProductListLoading) {
          return billingProfiles?.map?.(({ id, name }) => {
            return (
              <div key={id}>
                <BillingProfileNameWrapper>{name}</BillingProfileNameWrapper>
                <SkeletonItem isShimmering hasIcon />
                <SkeletonItem isShimmering hasIcon />
              </div>
            );
          });
        }
      } else {
        if (isBillingProfileLoading || isFetchEntitlementsLoading) {
          return (
            <div>
              {Array.from({ length: 8 }).map((_, index) => (
                <SkeletonItem key={index} isShimmering hasIcon />
              ))}
            </div>
          );
        }
      }

      /**
       * Here, loading is complete but search results are empty.
       * In such a case, we need to show the empty view as long as we do not fall in the transfer flow.
       */
      const isLoadingComplete = !isBillingProfileLoading && !isFetchEntitlementsLoading;

      if (
        isLoadingComplete &&
        !productsGroupedByBillingProfileAndCloudSite?.length &&
        !showTransferForm &&
        isEmpty(cloudSiteOrOrgNameMap)
      ) {
        return <NoEntitlement />;
      }

      if (productsGroupedByBillingProfileAndCloudSite) {
        const productsGroupedByBillingProfileAndCloudSiteSorted = productsGroupedByBillingProfileAndCloudSite?.sort(
          (a, b) => (a.billingProfileName < b.billingProfileName ? -1 : 1)
        );

        return productsGroupedByBillingProfileAndCloudSiteSorted?.map?.(
          ({ billingProfileId, billingProfileName, cloudSiteOrOrgNameMap }) => {
            return (
              <div key={billingProfileId}>
                {!isCheckboxSelectionEnabled && (
                  <BillingProfileNameWrapper>{billingProfileName}</BillingProfileNameWrapper>
                )}

                <ProductsGroupedByCloudSite
                  cloudSiteOrOrgNameMap={cloudSiteOrOrgNameMap}
                  externalProductsList={externalProductsList}
                  onChange={onChange}
                  showInactiveProducts={showInactiveProducts}
                  isSelectionDisabled={isSelectionDisabled}
                  onTransferSearchFormSubmit={onTransferSearchFormSubmit}
                  shouldShowAddons={shouldShowAddons}
                  shouldShowSandboxStatus={shouldShowSandboxStatus}
                  isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
                  isCheckboxSelectionEnabled={isCheckboxSelectionEnabled}
                  checkedItemMap={checkedItemMap}
                  setCheckedItemMap={setCheckedItemMap}
                  isSingleProductSelection={isSingleProductSelection}
                  isTWCPhase2Enabled={isTWCPhase2Enabled}
                  orgProductInstancesMap={orgProductInstancesMap}
                />
              </div>
            );
          }
        );
      }

      if (cloudSiteOrOrgNameMap) {
        return (
          <ProductsGroupedByCloudSite
            cloudSiteOrOrgNameMap={cloudSiteOrOrgNameMap}
            externalProductsList={externalProductsList}
            onChange={onChange}
            showInactiveProducts={showInactiveProducts}
            isSelectionDisabled={isSelectionDisabled}
            onTransferSearchFormSubmit={onTransferSearchFormSubmit}
            shouldShowAddons={shouldShowAddons}
            shouldShowSandboxStatus={shouldShowSandboxStatus}
            isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
            isCheckboxSelectionEnabled={isCheckboxSelectionEnabled}
            alreadyCheckedItemMap={alreadyCheckedItemMap}
            checkedItemMap={checkedItemMap}
            setCheckedItemMap={setCheckedItemMap}
            isDefaultExpanded={isDefaultExpanded}
            isSingleProductSelection={isSingleProductSelection}
            isTWCPhase2Enabled={isTWCPhase2Enabled}
            orgProductInstancesMap={orgProductInstancesMap}
          />
        );
      }
    }

    if (isLoading || isEntitlementListWithEmailLoading) return <>{renderLoading}</>;

    if (cloudSiteOrOrgNameMap && !Array.isArray(cloudSiteOrOrgNameMap)) {
      return (
        <>
          {isRenderingTransferFlowEntitlements && !hideFoundLabel && (
            <div className="flex align-center">
              <CheckCircleIcon label="" primaryColor="#22A06B" /> Found
            </div>
          )}
          <ProductsGroupedByCloudSite
            cloudSiteOrOrgNameMap={cloudSiteOrOrgNameMap}
            externalProductsList={externalProductsList}
            onChange={onChange}
            isSelectionDisabled={isSelectionDisabled}
            isRenderingTransferFlowEntitlements={isRenderingTransferFlowEntitlements}
            onTransferSearchFormSubmit={onTransferSearchFormSubmit}
            showInactiveProducts={showInactiveProducts}
            shouldShowAddons={shouldShowAddons}
            shouldShowSandboxStatus={shouldShowSandboxStatus}
            isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
            isCheckboxSelectionEnabled={isCheckboxSelectionEnabled}
            checkedItemMap={checkedItemMap}
            setCheckedItemMap={setCheckedItemMap}
            hideExternalListButton={hideExternalListButton}
            isDefaultExpanded={isDefaultExpanded}
            isSingleProductSelection={isSingleProductSelection}
            isTWCPhase2Enabled={isTWCPhase2Enabled}
            orgProductInstancesMap={orgProductInstancesMap}
          />
        </>
      );
    }

    if (showNoEntitlement) return <NoEntitlement />;

    return null;
  })();

  return isCheckboxSelectionEnabled ? (
    <CloudSiteStyles>
      <div
        className={classNames('cloud-site-container cloud-site-container-v2', {
          'cloud-site-container-transfer-entitlements': isRenderingTransferFlowEntitlements,
        })}
      >
        {content}
      </div>
    </CloudSiteStyles>
  ) : (
    <div
      className={classNames('cloud-site-container cloud-site-container-v2', {
        'cloud-site-container-transfer-entitlements': isRenderingTransferFlowEntitlements,
      })}
    >
      {content}
    </div>
  );
};
